import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertsService } from "../../services/AlertsService";
import { getRefetchIntervalMs } from "../../utils/refreshInterval";

export const useOrganisationAlerts = (orgId, skip = 0, limit = 100) => {
  const refetchIntervalMs = getRefetchIntervalMs();

  return useQuery({
    queryKey: ["organisationAlerts", orgId, skip, limit],
    queryFn: () => AlertsService.fetchOrganisationAlerts(orgId, skip, limit),
    enabled: !!orgId,
    staleTime: 1000 * 60 * 2,
    refetchInterval: refetchIntervalMs,
    placeholderData: (prev) => prev,
  });
};

export const useAlert = (alertId) => {
  return useQuery({
    queryKey: ["alert", alertId],
    queryFn: () => AlertsService.fetchAlert(alertId),
    enabled: !!alertId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AlertsService.createAlert,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["organisationAlerts"] });
      if (variables.deviceId) {
        queryClient.invalidateQueries({
          queryKey: ["deviceAlerts", variables.deviceId],
        });
      }
    },
  });
};

export const useUpdateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ alertId, data }) => AlertsService.updateAlert(alertId, data),
    onSuccess: (_, { alertId }) => {
      queryClient.invalidateQueries({ queryKey: ["alert", alertId] });
      queryClient.invalidateQueries({ queryKey: ["organisationAlerts"] });
      queryClient.invalidateQueries({ queryKey: ["deviceAlerts"] });
    },
  });
};

export const useDeleteAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AlertsService.deleteAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisationAlerts"] });
      queryClient.invalidateQueries({ queryKey: ["deviceAlerts"] });
    },
  });
};

export const useMarkAlertAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AlertsService.markAsRead,
    onSuccess: (_, alertId) => {
      queryClient.invalidateQueries({ queryKey: ["alert", alertId] });
      queryClient.invalidateQueries({ queryKey: ["organisationAlerts"] });
      queryClient.invalidateQueries({ queryKey: ["deviceAlerts"] });
    },
  });
};
